import { useCreateScriptComment } from "@entities/brief/lib/hooks/scripting/useCreateScriptComment";
import { useDeleteComment } from "@entities/brief/lib/hooks/scripting/useDeleteComment";
import { useResolveScriptComment } from "@entities/brief/lib/hooks/scripting/useResolveScriptComment";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import {
  getCommentQuote,
  getCommentThreadId,
} from "@features/ScriptConceptEditor/lib/commentAnchor";
import { ToastService } from "@shared/lib/services";
import {
  IScriptingComment,
  ScriptCommentedOnEnum,
} from "@shared/lib/types/briefCampains";
import { Input } from "@shared/ui";
import classNames from "classnames";
import dayjs from "dayjs";
import {
  CheckCircle2,
  CornerDownRight,
  MessageSquare,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";

interface ScriptCommentsProps {
  comments: IScriptingComment[];
  highlightId: string | null;
  scriptId?: string;
  quotedText?: string | null;
  pendingThreadId?: string | null;
  onClearQuotedText?: () => void;
  onSelectThread?: (threadId: string | null, quote?: string) => void;
  onCommentCreated?: (threadId: string, quote?: string) => void;
  onGetQuote?: (threadId: string) => string | null;
}

export default function ScriptComments(props: ScriptCommentsProps) {
  const {
    comments,
    highlightId,
    scriptId,
    quotedText,
    pendingThreadId,
    onClearQuotedText,
    onSelectThread,
    onCommentCreated,
    onGetQuote,
  } = props;

  const { userDetails } = useGetMe();
  const { createComment, isCreated } = useCreateScriptComment(scriptId || "");
  const { deleteComment, isDeleted, isDeleting, variables } = useDeleteComment(
    scriptId || ""
  );
  const { resolveComment, isResolving } = useResolveScriptComment(
    scriptId || ""
  );

  const isUserComment = (comment: IScriptingComment) => {
    return comment.createdBy?._id === userDetails?.userEntity?.id;
  };

  const [commentDraft, setCommentDraft] = useState<string>("");
  const commentsEndRef = useRef<HTMLDivElement>(null);

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommentDraft(e.target.value);
  };

  const handlePostComment = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!commentDraft.trim() || !scriptId) return;

    const threadId = pendingThreadId || undefined;
    const liveQuote =
      (threadId && onGetQuote?.(threadId)?.trim()) || quotedText?.trim() || "";
    const isCommentingOnSelection = Boolean(threadId && liveQuote);

    if (isCommentingOnSelection && threadId) {
      onCommentCreated?.(threadId, liveQuote);
    }

    createComment({
      scriptId,
      comment: commentDraft,
      commentedOn: isCommentingOnSelection
        ? ScriptCommentedOnEnum.Thread
        : ScriptCommentedOnEnum.General,
      threadId: isCommentingOnSelection ? threadId : undefined,
      quotedText: isCommentingOnSelection ? liveQuote : undefined,
      clientCommentId: threadId || `client-${Date.now()}`,
    });

    setCommentDraft("");
  };

  const handleCommentClick = (comment: IScriptingComment) => {
    const threadId = getCommentThreadId(comment);
    if (threadId) {
      onSelectThread?.(threadId, getCommentQuote(comment));
    }
  };

  const handleResolveComment = (
    e: React.MouseEvent,
    commentId: string,
    isResolved: boolean
  ) => {
    e.stopPropagation();
    if (!scriptId) return;
    resolveComment({
      scriptId,
      commentId,
      isResolved: !isResolved,
    });
  };

  const handleDeleteComment = (e: React.MouseEvent, commentId: string) => {
    e.stopPropagation();
    if (!scriptId) return;
    deleteComment({
      scriptId: scriptId || "",
      commentId,
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return dayjs(dateStr).format("MMM DD, h:mm A");
  };

  const getCommentType = (comment: IScriptingComment): string => {
    if (comment.commentedOn === ScriptCommentedOnEnum.Thread) {
      return "Thread";
    }
    if (comment.commentedOn === ScriptCommentedOnEnum.Concept) {
      return "Concept";
    }
    if (comment.commentedOn === ScriptCommentedOnEnum.Timeframe) {
      return "Block";
    }
    return "General";
  };

  const getCommentBlockId = (comment: IScriptingComment): string | null => {
    return getCommentThreadId(comment);
  };

  const getQuoteLabel = (comment: IScriptingComment): string => {
    const threadId = getCommentThreadId(comment);
    const storedQuote = getCommentQuote(comment);
    const liveQuote = threadId ? onGetQuote?.(threadId)?.trim() : "";
    const quote = storedQuote || liveQuote || "";
    if (!quote) return "Concept";
    return `"${quote.slice(0, 48)}${quote.length > 48 ? "…" : ""}"`;
  };

  useEffect(() => {
    if (isCreated) {
      setCommentDraft("");
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [isCreated]);

  useEffect(() => {
    if (isDeleted) {
      ToastService.success("Comment Deleted");
    }
  }, [isDeleted]);

  return (
    <div className={styles.commentsContainer}>
      {/* Header */}
      <div className={styles.commentsHeader}>
        <h4 className={styles.commentsHeaderTitle}>
          <MessageSquare size={14} className={styles.commentsHeaderIcon} />{" "}
          Comments
          <span className={styles.commentsCountBadge}>{comments.length}</span>
        </h4>
      </div>

      {/* List */}
      <div className={styles.commentsList}>
        {comments.length === 0 ? (
          <div className={styles.commentsEmpty}>
            <MessageSquare size={24} className={styles.commentsEmptyIcon} />
            <p className={styles.commentsEmptyText}>No comments yet.</p>
          </div>
        ) : (
          <div className={styles.commentsListContent}>
            {comments.map((comment) => {
              const isOwner = isUserComment(comment);
              const blockId = getCommentBlockId(comment);
              const isHighlighted = blockId && highlightId === blockId;
              const isResolved = comment.isResolved ?? false;
              const authorName = comment.createdBy
                ? `${comment.createdBy.firstName || ""} ${
                    comment.createdBy.lastName || ""
                  }`.trim() || "Unknown"
                : "Unknown";
              const commentType = getCommentType(comment);

              return (
                <div
                  key={comment._id || comment.clientCommentId}
                  className={classNames(styles.commentItem, {
                    [styles.commentItemHighlighted]: isHighlighted,
                    [styles.commentItemResolved]: isResolved,
                  })}
                  onClick={() => handleCommentClick(comment)}
                >
                  {blockId && <div className={styles.commentIndicatorLine} />}

                  <div className={styles.commentHeader}>
                    <div className={styles.commentAuthor}>
                      <span className={styles.commentAuthorName}>
                        {authorName}
                      </span>
                      <span className={styles.commentType}>
                        ({commentType})
                      </span>
                    </div>
                    <span className={styles.commentDate}>
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>

                  <p
                    className={classNames(styles.commentText, {
                      [styles.commentTextResolved]: isResolved,
                    })}
                  >
                    {comment.comment}
                  </p>

                  <div className={styles.commentFooter}>
                    {blockId ? (
                      <div className={styles.commentBlockBadge}>
                        {comment.commentedOn ===
                          ScriptCommentedOnEnum.Concept ||
                        comment.commentedOn === ScriptCommentedOnEnum.Thread ||
                        getCommentQuote(comment) ? (
                          <>
                            <Sparkles size={10} />
                            <span className={styles.commentQuoteText}>
                              {getQuoteLabel(comment)}
                            </span>
                          </>
                        ) : (
                          <>
                            <CornerDownRight size={10} /> {commentType}
                          </>
                        )}
                      </div>
                    ) : (
                      <div></div>
                    )}

                    <div className={styles.commentActions}>
                      {isOwner && (
                        <>
                          <button
                            onClick={(e) =>
                              handleResolveComment(
                                e,
                                comment._id || "",
                                isResolved
                              )
                            }
                            className={classNames(styles.commentActionButton, {
                              [styles.commentActionButtonResolved]: isResolved,
                            })}
                            title={isResolved ? "Unresolve" : "Resolve"}
                            disabled={isResolving}
                          >
                            <CheckCircle2 size={14} />
                          </button>
                          <button
                            onClick={(e) =>
                              handleDeleteComment(e, comment._id || "")
                            }
                            className={styles.commentActionButtonDelete}
                            title="Delete"
                            disabled={
                              isDeleting && variables?.commentId === comment._id
                            }
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={commentsEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className={styles.commentInputArea}>
        {quotedText && (
          <div className={styles.commentReplyIndicator}>
            <CornerDownRight
              size={10}
              className={styles.commentReplyIndicatorIcon}
            />
            <span className={styles.commentReplyIndicatorTarget}>
              {quotedText}
            </span>
            <button
              type="button"
              onClick={onClearQuotedText}
              className={styles.commentReplyIndicatorClose}
              title="Cancel"
            >
              <X size={12} />
            </button>
          </div>
        )}
        <form onSubmit={handlePostComment} className={styles.commentForm}>
          <Input
            id="comment-input"
            value={commentDraft}
            onChange={handleCommentChange}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handlePostComment();
              }
            }}
            // className={styles.commentTextarea}
            placeholder="Add comment..."
            // disabled={isCreating}
            suffix={
              <button
                type="submit"
                disabled={!commentDraft.trim()}
                className={styles.commentSendButton}
              >
                <Send size={12} />
              </button>
            }
          />
        </form>
      </div>
    </div>
  );
}
